/**
 * RINF status helpers: file location, labels and the filters of the mandatory
 * parameters table.
 */

import env from '@config/env';
import { humanizeUriLocalName } from '@utils/uriDisplay.utils';
import type { RinfMandatoryParameter, RinfParameterStatus } from '@/types';
import type { RinfStatusCounts } from '@/types';

const ERA_NAMESPACE = 'http://data.europa.eu/949/';

export const RINF_STATUSES: RinfParameterStatus[] = ['covered', 'notYetAvailable', 'missing'];
export const RINF_DISPLAY_STATUSES: Array<'covered' | 'missing'> = ['covered', 'missing'];

export type RinfDisplayStatus = (typeof RINF_DISPLAY_STATUSES)[number];

export function displayStatus(status: RinfParameterStatus): RinfDisplayStatus {
  return status === 'covered' ? 'covered' : 'missing';
}

export function missingCount(counts: Pick<RinfStatusCounts, 'missing' | 'notYetAvailable'>): number {
  return counts.missing + counts.notYetAvailable;
}

export function rinfStatusFileUrl(): string {
  return `${env.rinfStatusBase.replace(/\/+$/, '')}/rinf-status.json`;
}

/** "http://data.europa.eu/949/Track" → "era:Track"; other IRIs are returned as they are. */
export function eraCurie(iri: string): string {
  return iri.startsWith(ERA_NAMESPACE) ? `era:${iri.slice(ERA_NAMESPACE.length)}` : iri;
}

/** Label from the ERA ontology; falls back to the humanized local name. */
export function termLabel(item: { iri: string; curie?: string | null; label?: string }): string {
  return item.label || humanizeUriLocalName(item.iri) || item.curie || item.iri;
}

/**
 * i18n key of a subsystem, e.g. era:ccsSubsystemDataParameter →
 * "rinfStatus.subsystems.ccsSubsystemDataParameter". The ontology label is the
 * default value, so a subsystem without a translation still has a name.
 */
export function subsystemKey(iri: string | null): string {
  if (!iri) return 'rinfStatus.subsystems.none';
  return `rinfStatus.subsystems.${iri.startsWith(ERA_NAMESPACE) ? iri.slice(ERA_NAMESPACE.length) : iri}`;
}

/**
 * The parameter groups between the subsystem and the parameter, e.g. for
 * era:trackId [root, era:infraSubsystemDataParameter, era:trackParameter] →
 * [era:trackParameter]. Empty when the parameter sits directly under its subsystem.
 */
export function pathBelowSubsystem(path: string[]): string[] {
  return path.slice(2);
}

/** Human-readable name of a parameter group, e.g. "…/trackParameter" → "Track parameter". */
export function groupLabel(iri: string): string {
  return humanizeUriLocalName(iri) ?? eraCurie(iri);
}

/** Published properties whose label, prefixed name or description contains the search text. */
export function filterProperties<T extends { iri: string; curie: string; label: string; description: string }>(
  properties: T[],
  search: string,
): T[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return properties;
  return properties.filter(p => termLabel(p).toLowerCase().includes(needle)
    || p.curie.toLowerCase().includes(needle)
    || p.description.toLowerCase().includes(needle));
}

/** Share of the mandatory parameters in a status, 0 when there are none. */
export function share(part: number, total: number): number {
  return total > 0 ? part / total : 0;
}

export type RinfStatusFilter = 'all' | RinfDisplayStatus;

export interface RinfParameterFilters {
  search: string;
  /** Subsystem IRI, '' for all, 'none' for parameters without a subsystem */
  subsystem: string;
  status: RinfStatusFilter;
}

/** No filters: every mandatory parameter, so the table adds up to the overview. */
export const DEFAULT_PARAMETER_FILTERS: RinfParameterFilters = {
  search: '',
  subsystem: '',
  status: 'all',
};

export function hasActiveFilters(filters: RinfParameterFilters): boolean {
  return filters.search.trim() !== ''
    || filters.subsystem !== DEFAULT_PARAMETER_FILTERS.subsystem
    || filters.status !== DEFAULT_PARAMETER_FILTERS.status;
}

function matchesStatus(status: RinfParameterStatus, filter: RinfStatusFilter): boolean {
  if (filter === 'all') return true;
  return displayStatus(status) === filter;
}

export function filterParameters(
  parameters: RinfMandatoryParameter[],
  filters: RinfParameterFilters,
): RinfMandatoryParameter[] {
  const needle = filters.search.trim().toLowerCase();
  return parameters.filter(parameter => {
    if (!matchesStatus(parameter.status, filters.status)) return false;
    if (filters.subsystem === 'none' ? parameter.subsystem !== null
      : filters.subsystem && parameter.subsystem !== filters.subsystem) return false;
    if (!needle) return true;
    return termLabel(parameter).toLowerCase().includes(needle)
      || parameter.curie.toLowerCase().includes(needle)
      || parameter.description.toLowerCase().includes(needle);
  });
}

const STATUS_ORDER: Record<RinfDisplayStatus, number> = { missing: 0, covered: 1 };

/** Missing first, then covered; by label within each. */
export function sortParameters(parameters: RinfMandatoryParameter[], locale: string): RinfMandatoryParameter[] {
  return [...parameters].sort((a, b) => STATUS_ORDER[displayStatus(a.status)] - STATUS_ORDER[displayStatus(b.status)]
    || termLabel(a).localeCompare(termLabel(b), locale));
}
